import { Table as TanTable } from '@tanstack/react-table';
import { GroupedRow, HeaderCell, TableCell, TableLayout } from './tableWithFixedHeaderLayout';
import { useFixedHeaderScrollSync } from './useFixedHeaderScrollSync';
import { useFixedHeaderColumnsLayout } from './useFixedHeaderColumnsLayout';

type AppTableWithFixedHeaderProps<D extends object = object> = {
  className?: string;
  table: TanTable<D>;
  testId?: string;
};

/**
 * A table component with a fixed header and fixed first column.
 * We use this table for displaying data with a fixed header and first column, allowing users to scroll horizontally through the remaining columns.
 */
export const AppTableWithFixedHeader = <D extends object = object>({
  table,
  className,
  testId,
}: AppTableWithFixedHeaderProps<D>) => {
  const { firstHeader, restHeaders, firstColumnWidth, scrollableColumnsTemplate, scrollableContentWidth } =
    useFixedHeaderColumnsLayout(table);

  const { headerScrollRef, bodyFirstColumnRef, bodyScrollRef, hasBodyHorizontalScroll, handleScroll } =
    useFixedHeaderScrollSync(table);

  return (
    <TableLayout.Table
      columnCount={table.getVisibleLeafColumns().length}
      firstColumnWidth={firstColumnWidth}
      scrollableColumnsTemplate={scrollableColumnsTemplate || undefined}
      testId={testId}
      className={className}
    >
      <TableLayout.Table.Header>
        <TableLayout.Table.Header.FirstColumn>
          <HeaderCell key={firstHeader.id} header={firstHeader} />
        </TableLayout.Table.Header.FirstColumn>
        <TableLayout.Table.Header.ScrollableColumns ref={headerScrollRef}>
          {restHeaders.map((header) => (
            <HeaderCell key={header.id} header={header} />
          ))}
        </TableLayout.Table.Header.ScrollableColumns>
      </TableLayout.Table.Header>
      <TableLayout.Table.Body>
        <TableLayout.Table.Body.FirstColumn ref={bodyFirstColumnRef} hasHorizontalScroll={hasBodyHorizontalScroll}>
          {table.getRowModel().rows.map((row) => (
            <GroupedRow key={row.id} row={row}>
              {(cells) => cells.slice(0, 1).map((cell) => <TableCell key={cell.id} cell={cell} />)}
            </GroupedRow>
          ))}
        </TableLayout.Table.Body.FirstColumn>
        <TableLayout.Table.Body.ScrollableColumns ref={bodyScrollRef} onScroll={handleScroll}>
          {table.getRowModel().rows.map((row) => (
            <GroupedRow key={row.id} row={row} hideGroupingValue contentWidth={scrollableContentWidth}>
              {(cells) => cells.slice(1).map((cell) => <TableCell key={cell.id} cell={cell} />)}
            </GroupedRow>
          ))}
        </TableLayout.Table.Body.ScrollableColumns>
      </TableLayout.Table.Body>
    </TableLayout.Table>
  );
};
